import { useEffect, useCallback, useState } from "react";

import { isInProductJourneySubAnchor } from "src/pages/software/confluence/anchors/subAnchors/subAnchor-util";

import { ConfluenceBackground } from "@/components/signup/confluence-background";
import { useSearchParameters } from "@/components/signup/hooks";
import {
    UfoCustomTimingKey,
    UfoCustomTimingWrapper,
    ufoEventRedirectToProduct
} from "@/components/signup/utils";
import { useUserContext } from "@/components/user-context";
import { useBxpWebStargateClient } from "@/libraries/bxp-web-stargate-client/src";
import { useFeatureExposed } from "@/utils/feature-exposed";

import { useCloudStatus, useCloudStatusRedirection } from "../../hooks";

import { LoadingAnimation } from "./loading-animation";

export const ConfluenceProvisioningUi = ({
    subAnchor
}: {
    subAnchor?: string;
}) => {
    const { triggerOnSuccess, startPerceivedProvisioningTimer, cloudId } =
        useCloudStatus();
    const { redirectUrl } = useCloudStatusRedirection();
    const {
        profile: { attributes, cohorts }
    } = useUserContext();
    const stargateClient = useBxpWebStargateClient();
    const { templateId } = useSearchParameters();
    const hasTargetedSubAnchor = isInProductJourneySubAnchor(subAnchor);

    const [isRovoActive, setIsRovoActive] = useState(false);
    const [attempts, setAttempts] = useState(0);
    const MAX_RETRIES = 20;

    const sites = attributes?.existingSites;
    const hasOnlyConfluenceSites = sites
        ? sites.every((site) => {
              const products = site.products;
              return products.every(
                  ({ productName }) => productName === "confluence"
              );
          })
        : true;

    const isXflow = !hasOnlyConfluenceSites;

    const isEligibleForTailorSubAnchorJourneyExp =
        !isXflow && !templateId && hasTargetedSubAnchor;

    const callbackForRovoCheck = useCallback(async () => {
        if (
            cohorts?.domainType === "business-domain" &&
            cloudId &&
            attempts < MAX_RETRIES
        ) {
            // biz premium needs to check to see if rovo is done being provisioned
            const { products } =
                await stargateClient.getLicenceInformationStatus({ cloudId });
            const edition = products["confluence.ondemand"]?.edition;
            if (edition === "premium") {
                const rovoData = products["rovo.ondemand"];
                if (rovoData?.state === "ACTIVE") {
                    setIsRovoActive(true);
                    ufoEventRedirectToProduct.success({
                        customData: {
                            reactComponent: "ConfluenceLayout default",
                            analyticsEvent: "redirected",
                            redirectFrom: "confirmationPage",
                            provisioningStatus: "success"
                        }
                    });
                    window.location.assign(redirectUrl);
                }
            } else {
                // if biz but not premium just proceede to confluence
                ufoEventRedirectToProduct.success({
                    customData: {
                        reactComponent: "ConfluenceLayout default",
                        analyticsEvent: "redirected",
                        redirectFrom: "confirmationPage",
                        provisioningStatus: "success"
                    }
                });
                window.location.assign(redirectUrl);
            }
        } else if (attempts === MAX_RETRIES) {
            // after max retries we want to proceed
            ufoEventRedirectToProduct.success({
                customData: {
                    reactComponent: "ConfluenceLayout default",
                    analyticsEvent: "redirected",
                    redirectFrom: "confirmationPage",
                    provisioningStatus: "success"
                }
            });
            window.location.assign(redirectUrl);
        } else {
            // if not biz we want to proceed
            ufoEventRedirectToProduct.success({
                customData: {
                    reactComponent: "ConfluenceLayout default",
                    analyticsEvent: "redirected",
                    redirectFrom: "confirmationPage",
                    provisioningStatus: "success"
                }
            });
            window.location.assign(redirectUrl);
        }
    }, [cohorts, stargateClient, redirectUrl, attempts, cloudId]);

    useFeatureExposed(
        "cfind-3233-in-product-sub-anchors-journeys",
        "enrolled",
        { conditions: [isEligibleForTailorSubAnchorJourneyExp] }
    );

    useEffect(() => {
        startPerceivedProvisioningTimer();
    }, [startPerceivedProvisioningTimer]);

    useEffect(() => {
        // check for rovo done every 3 seconds for max of 5 times before redirecting after success signa;
        if (
            !isRovoActive &&
            attempts <= MAX_RETRIES &&
            redirectUrl &&
            triggerOnSuccess
        ) {
            callbackForRovoCheck();
            const timer = setTimeout(() => {
                setAttempts((prev) => prev + 1);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [
        isRovoActive,
        attempts,
        redirectUrl,
        callbackForRovoCheck,
        triggerOnSuccess
    ]);

    return (
        <div data-testid="confluence-provisioning-ui">
            <UfoCustomTimingWrapper
                ufoEvent={ufoEventRedirectToProduct}
                ufoCustomTimingKey={
                    UfoCustomTimingKey.CONFLUENCE_ONBOARDING_SPINNER_DWELL_TIME
                }
            >
                <ConfluenceBackground />
                <LoadingAnimation isLoadingComplete={triggerOnSuccess} />
            </UfoCustomTimingWrapper>
        </div>
    );
};
